import { connect } from 'react-redux';
import {
  clearAnnotationSelection,
  setAnnotationSelection,
  setFocusedAnnotationId,
  setRelationshipViewFocusType,
} from '../../annotations/actions/ui-annotations.actions';
import {
  addToChainNesting,
  selectAnnotationsOfType,
  setSelectedAnnotations,
  toggleAnnotationSelection,
} from '../../annotations/actions/annotations.actions';
import { createAnnotationRelation } from '../../annotations/actions/annotation-relations.actions';
import { AnnotationSelection } from '../../annotations/models/annotation-selection.model';
import {
  selectCurrentPageOffsetRange,
  selectFullText,
  selectTextOffsets,
} from '../../annotations/selectors/annotation-pages.selectors';
import {
  selectAnnotationHighlights,
  selectEmphasizeAnnotationId,
} from '../../annotations/selectors/ui.selectors';
import { AppState } from '../../app-state';
import { selectJwt } from '../../auth/selectors/auth.selectors';
import {
  sourceDocumentError,
  sourceDocumentLoaded,
} from '../actions/source-document.actions';
import {
  selectSourceDocumentCurrentPageNumber,
  selectSourceDocumentError,
  selectSourceDocumentIsEditable,
  selectSourceDocumentLoading,
  selectSourceDocumentUrl,
} from '../selectors/source-document.selectors';
import { PdfViewer } from '../components/pdf-viewer.component';
import {
  selectPdfViewerRotation,
  selectPdfViewerSelectionMode,
  selectPdfViewerZoom,
} from '../selectors/ui.selecetors';
import { RELATION_TYPES } from '../../lib/relations/relation-types';
import { handleAnnotationClick } from '../../annotations/utils/annotation-selection';

function isOnPage(meta: any, pageStart: number, pageEnd: number): boolean {
  if (meta.discontinuous && meta.discontinuousSegments?.length > 0) {
    return meta.discontinuousSegments.some(
      (seg: any) =>
        typeof seg.start === 'number' &&
        typeof seg.end === 'number' &&
        seg.end > pageStart &&
        seg.start < pageEnd,
    );
  }
  if (typeof meta.start !== 'number' || typeof meta.end !== 'number')
    return false;
  return meta.end > pageStart && meta.start < pageEnd;
}

const mapStateToProps = (state: AppState) => ({
  page: selectSourceDocumentCurrentPageNumber(state),
  url: selectSourceDocumentUrl(state),
  authToken: selectJwt(state),
  loading: selectSourceDocumentLoading(state),
  error: selectSourceDocumentError(state),
  highlights: selectAnnotationHighlights(state),
  documentText: selectFullText(state),
  rotation: selectPdfViewerRotation(state),
  zoom: selectPdfViewerZoom(state),
  selectionMode: selectPdfViewerSelectionMode(state),
  emphasizeAnnotationId: selectEmphasizeAnnotationId(state),
  textOffsets: selectTextOffsets(state),
  pageOffsetRange: selectCurrentPageOffsetRange(state),
  selectedAnnotations: state.annotations.selectedAnnotations,
  chainNestingOrder: state.annotations.chainNestingOrder,
  annotations: state.annotations.metadata,
  annotationTypes: state.annotations.types,
  editable: selectSourceDocumentIsEditable(state),
  relationshipViewActive: state._UI_annotations.relationshipViewActive,
  relationshipViewFocusType: state._UI_annotations.relationshipViewFocusType,
  annotationRelations: Array.from(state.annotationRelations.entities.values()),
});

const mapDispatchToProps = (dispatch: any) => ({
  onDocumentLoad: (pdf: any) => {
    dispatch(sourceDocumentLoaded(pdf.numPages));
  },
  onError: (error: string) => {
    dispatch(sourceDocumentError(error));
  },
  onSelectionChange: (selection: AnnotationSelection[]) => {
    dispatch(setAnnotationSelection(selection, 'pdf-viewer'));
  },
  onSelectionCleared: () => dispatch(clearAnnotationSelection()),
  dispatchSetSelectedAnnotations: (ids: string[]) =>
    dispatch(setSelectedAnnotations(ids)),
  dispatchFocusAnnotation: (id: string) => dispatch(setFocusedAnnotationId(id)),
  dispatchAddToChainNesting: (id: string) => dispatch(addToChainNesting(id)),
  dispatchToggleAnnotationSelection: (id: string) =>
    dispatch(toggleAnnotationSelection(id)),
  dispatchSelectAnnotationsOfType: (type: string) =>
    dispatch(selectAnnotationsOfType(type)),
  dispatchSetRelationshipViewFocusType: (type: string | null) =>
    dispatch(setRelationshipViewFocusType(type)),
  dispatchCreateRelations: (pairs: { left: string; right: string }[]) => {
    dispatch(
      createAnnotationRelation(
        pairs.map(p => ({ ...p, relationType: RELATION_TYPES.CHILD_OF })),
      ),
    );
  },
});

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => {
  const {
    dispatchSetSelectedAnnotations,
    dispatchAddToChainNesting,
    dispatchToggleAnnotationSelection,
    dispatchCreateRelations,
    dispatchFocusAnnotation,
    dispatchSetRelationshipViewFocusType,
    ...restDispatch
  } = dispatchProps;
  const { selectedAnnotations, chainNestingOrder } = stateProps;

  const handlers = {
    setSelectedAnnotations: dispatchSetSelectedAnnotations,
    toggleAnnotationSelection: dispatchToggleAnnotationSelection,
    addToChainNesting: dispatchAddToChainNesting,
    createRelations: dispatchCreateRelations,
    focusAnnotation: dispatchFocusAnnotation,
  };

  return {
    ...ownProps,
    ...stateProps,
    ...restDispatch,
    onAnnotationClick: (annotationId: string, e: React.MouseEvent) =>
      handleAnnotationClick(
        annotationId,
        e,
        selectedAnnotations,
        chainNestingOrder.length > 0,
        handlers,
      ),
    onSelectAnnotations: dispatchSetSelectedAnnotations,
    onSelectAnnotationsOfType: (type: string) => {
      // Only select annotations of this type on the current page
      const range: [number, number] | null = stateProps.pageOffsetRange;
      const types: Map<string, string> | undefined = stateProps.annotationTypes;
      const meta: Map<string, any> | undefined = stateProps.annotations;
      const typeIds: string[] = [];
      if (types && meta) {
        types.forEach((t: string, id: string) => {
          if (t !== type) return;
          const m = meta.get(id);
          if (range) {
            if (!m) return;
            if (!isOnPage(m, range[0], range[1])) return;
          }
          typeIds.push(id);
        });
      }
      const existing = new Set(selectedAnnotations);
      const allSelected =
        typeIds.length > 0 && typeIds.every((id: string) => existing.has(id));
      if (allSelected) {
        const removeSet = new Set(typeIds);
        dispatchSetSelectedAnnotations(
          selectedAnnotations.filter((id: string) => !removeSet.has(id)),
        );
      } else {
        const newIds = typeIds.filter((id: string) => !existing.has(id));
        dispatchSetSelectedAnnotations(selectedAnnotations.concat(newIds));
      }
    },
    onClearAnnotationSelection: () => {
      if (selectedAnnotations.length > 0) {
        dispatchSetSelectedAnnotations([]);
      }
    },
    onRelationshipViewClick: dispatchSetRelationshipViewFocusType,
    onFocusAnnotation: dispatchFocusAnnotation,
  };
};

export const PdfViewerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(PdfViewer);
